import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { AREAS, formatDate } from "@/lib/constants";
import EventCard from "@/components/EventCard";
import { ArrowLeft, MapPin, Mail } from "lucide-react";

export default function ProgettoDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/slug/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="container-vv py-24 text-center"><p className="text-[#292522]/70">Progetto non trovato.</p><Link to="/progetti" className="btn-primary mt-4 inline-flex">Torna ai progetti</Link></div>;
  if (!data) return <div className="container-vv py-24 text-center text-[#292522]/50">Caricamento…</div>;

  const { project, events, articles } = data;
  const area = AREAS.find((a) => a.key === project.area);
  const cover = project.cover_image ? fileUrl(project.cover_image) : null;

  return (
    <div data-testid="progetto-detail-page">
      <section className="bg-[#FFF1E8] py-16">
        <div className="container-vv">
          <Link to="/progetti" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100] mb-6"><ArrowLeft className="h-4 w-4" /> Tutti i progetti</Link>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              {area && <span className="inline-block rounded-full bg-white/90 text-[#216B65] text-xs font-semibold px-3 py-1">{area.label}</span>}
              <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight text-[#292522] leading-[1.05]">{project.title}</h1>
              {project.subtitle && <p className="mt-3 text-xl text-[#292522]/70 italic">{project.subtitle}</p>}
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] rounded-[2rem] overflow-hidden bg-white shadow-[0_20px_60px_rgba(41,37,34,0.12)]">
                {cover ? <img src={cover} alt={project.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><div className="w-40 h-40 blob bg-[#FF6100]/25" /></div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-vv py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 prose max-w-none">
          <h2 className="font-display text-3xl font-medium text-[#292522]">Cosa è</h2>
          <p className="text-lg text-[#292522]/80 leading-relaxed whitespace-pre-line">{project.short_desc}</p>
          {project.full_desc && (
            <>
              <h3 className="font-display text-2xl font-medium text-[#292522] mt-8">Nel dettaglio</h3>
              <p className="text-[#292522]/80 leading-relaxed whitespace-pre-line">{project.full_desc}</p>
            </>
          )}
        </div>
        <aside className="lg:col-span-4">
          <div className="card-vv p-6 bg-[#F8F1E8]">
            <h4 className="eyebrow">Info</h4>
            <dl className="mt-4 space-y-3 text-sm">
              <div><dt className="text-[#292522]/60">Stato</dt><dd className="font-semibold">{project.status}</dd></div>
              {project.audience && <div><dt className="text-[#292522]/60">Pubblico</dt><dd>{project.audience}</dd></div>}
              {project.contact_email && <div><dt className="text-[#292522]/60">Contatto</dt><dd><a href={`mailto:${project.contact_email}`} className="text-[#FF6100] inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {project.contact_email}</a></dd></div>}
            </dl>
          </div>
        </aside>
      </section>

      {events?.length > 0 && (
        <section className="container-vv pb-16">
          <h2 className="font-display text-3xl font-medium text-[#292522] mb-6">Eventi collegati</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((ev, i) => <EventCard key={ev.id} event={ev} testId={`project-event-${i}`} />)}
          </div>
        </section>
      )}

      {articles?.length > 0 && (
        <section className="container-vv pb-24">
          <h2 className="font-display text-3xl font-medium text-[#292522] mb-6">Articoli e storie</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {articles.map((a) => (
              <div key={a.id} className="card-vv p-6 bg-white">
                <span className="inline-block rounded-full bg-[#FFF1E8] text-[#FF6100] text-xs font-semibold px-3 py-1">{a.category}</span>
                <h3 className="mt-3 font-display text-xl font-medium">{a.title}</h3>
                <p className="mt-2 text-xs text-[#292522]/50">{formatDate(a.published_at)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
